// apps/web/src/wizard/queries.ts — Phase 4 plan 04-03.
//
// GraphQL operations for the guest reservation wizard (A4..A7). The
// existing `apps/web/src/pages/reservation/operations.ts` already
// exports AVAILABILITY_QUERY and SLOTS_QUERY for the date + slot
// pre-wizard steps; the wizard needs the singleton restaurant
// profile (for the consent text + manual-mode flag) and the public
// `createReservation` mutation.
//
// Per the existing project pattern we use `graphql-request`'s `gql`
// tag + the `gqlRequest` wrapper in `apps/web/src/lib/api.ts`. The
// 401-retry interceptor in lib/api.ts skips the silent-refresh
// retry for unknown operations, which is fine: the public booking
// mutation never returns 401 (no Authorization header is sent).

import { gql } from "graphql-request";

/**
 * Singleton restaurant profile. The wizard renders the consent text
 * (DE/EN) from `consentText`; the `confirmationMode` flag flips A6
 * between "Verbindlich reservieren" (auto) and "Anfrage senden"
 * (manual). `areasEnabled === false` disables the area picker on A5
 * (Phase 5 will add an explicit area step; the plan keeps `areaId`
 * nullable so the absence is encoded in the input).
 */
export const GET_RESTAURANT = gql`
  // Wizard-facing read of the singleton restaurant profile. Switched
  // from Query.restaurant to Query.publicBookingContext in the
  // Phase-4 schema-gap fix (item 1 in the follow-ups list): the admin
  // restaurant query is RBAC-gated and would 403 the unauthenticated
  // wizard, while publicBookingContext exposes only the PII-free
  // subset (id, slug, timezone, confirmationMode, areasEnabled,
  // consentText). See apps/api/src/graphql/resolvers/settings.ts
  // (publicBookingContext resolver) for the narrowing.
  query GetRestaurant {
    publicBookingContext {
      id
      slug
      timezone
      confirmationMode
      areasEnabled
      consentText {
        de
        en
      }
    }
  }
`;

/**
 * Bookable slot decision for a single service date + party size.
 * The server's `Query.slots(date)` already returns `remainingSeats`
 * per slot; the wizard adds `startsAt` + `durationMinutes` so the
 * createReservation input can be built without a second round-trip.
 * Slots with `remainingSeats === 0` are HIDDEN by the server
 * (ROADMAP success criterion #3: full slots hidden, not
 * rendered-but-disabled); the wizard surfaces "Keine verfügbaren
 * Zeitpunkte" when the result is empty.
 *
 * `partySize` is held client-side only. It is NOT a GraphQL variable
 * here because the current `slots(date)` resolver does not filter by
 * party size — declaring `$partySize` without referencing it triggers
 * GraphQL's "Variable is never used" validation rule and the query
 * fails (the previous `GET_AVAILABLE_SLOTS` document had this exact
 * bug and A4 returned a 200-with-errors on every load).
 */
export const GET_AVAILABLE_SLOTS = gql`
  query GetAvailableSlots($date: String!) {
    slots(date: $date) {
      id
      startTime
      startsAt
      endsAt
      durationMinutes
      remainingSeats
      weekday
    }
  }
`;

/**
 * Public createReservation mutation. The resolver lives in
 * `apps/api/src/graphql/resolvers/reservation-public.ts`; the
 * response carries the raw `selfServiceToken` (the only place the
 * literal token exists — the DB stores Argon2id PHC + SHA-256
 * digest, never the raw). The web client must immediately
 * `history.replaceState(null, '', '/reservierung/bestaetigung')` to
 * scrub the token from the URL after navigating to A7.
 */
export const CREATE_RESERVATION = gql`
  mutation CreateReservation(
    $input: CreateReservationInput!
    $captchaToken: String
  ) {
    createReservation(input: $input, captchaToken: $captchaToken) {
      reservationNumber
      selfServiceToken
      selfServiceUrl
      expiresAt
    }
  }
`;
